import { inject, Injectable } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { _, TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { IPosition } from '@app/core/interfaces/position.interface';
import { ClipboardCopyService } from '@app/core/services/clipboard-copy/clipboard-copy.service';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { DoToolbarService } from '@app/main/main-content/display-options/services/do-toolbar/do-toolbar.service';
import { contextMenuTodo } from '@app/main/main-content/context-menu/utils/context-menu-todo';
import { PermissionName } from '@app/core/permissions/enums/permission-name.enum';
import { LdContextMenuEventsService } from '@app/main/leak-detection/context-menus/services/ld-context-menu-events.service';
import {
  LdActionPlace,
  LdCoordinatorDispatch,
} from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.actions';

@Injectable({ providedIn: 'root' })
export class LdContextMenuMapAreaService {
  private readonly translateService = inject(TranslateService);
  private readonly ngxPermissionsService = inject(NgxPermissionsService);
  private readonly formattingData = inject(FormattingDataService);
  private readonly clipboardCopy = inject(ClipboardCopyService);
  private readonly doToolbarService = inject(DoToolbarService);
  private readonly contextMenuEvents = inject(LdContextMenuEventsService);

  public getMapItems(position: IPosition, dispatch: LdCoordinatorDispatch): MenuItem[] {
    if (!this.canAccessMapArea()) return [];

    const linkClass = '!py-[0.59375rem]';
    const canEditMap = !!this.ngxPermissionsService.getPermission(PermissionName.LeakDetection_Write_MapEditing);

    const mapEditingItems: MenuItem[] = canEditMap
      ? [
          { ...this.contextMenuEvents.getCreateEventMenuItem(position, LdActionPlace.Map, dispatch), linkClass },
          {
            label: this.translateService.instant(_(`@@leak-detection-context-menu-create-pipe__label`)),
            icon: 'icons/icon-create-pipe.svg',
            linkClass,
            command: () => contextMenuTodo('Create pipe', position),
          },
        ]
      : [];

    return [
      ...mapEditingItems,
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-copy-coordinates__label`)),
        icon: 'icons/icon-copy-coordinates.svg',
        linkClass,
        command: () => this.clipboardCopy.copy(this.formattingData.formatCoordinates(position)),
      },
      {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-zoom-to-fit__label`)),
        icon: 'icons/icon-zoom-to-fit.svg',
        linkClass,
        // same toolbar toggle the display-options button uses — it fits everything currently shown
        command: () => this.doToolbarService.toggleZoomToFit(),
      },
    ];
  }

  private canAccessMapArea(): boolean {
    return !!this.ngxPermissionsService.getPermission(PermissionName.LeakDetection_Read_MapAreaContextMenu);
  }
}
